import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {Icon} from '@forecast-it/design-system';
import {
	BUILDER_FILE_REJECT_REASON,
	trackBaselineBuilderDocumentUploaded,
	trackBaselineBuilderFileRejected,
} from '../../../tracking/amplitude/baselineBuilderEvents';
import {
	NOVA_PURPLE,
	ERROR_COLOR_2,
	BG_WHITE,
	BORDER_INPUT,
	BG_PANEL,
	ICON_BOX_BG,
	TEXT_MAIN,
	TEXT_AUX,
	BORDER_DIVIDER,
	SUCCESS_BRIGHT,
	BORDER_DOT_PENDING,
	BG_DOT_PENDING,
	ERROR_BG,
	TEXT_MED,
} from './colors';
import type {AttachedFile, SowExtractionStep} from './types';
import {SOW_CHECKLIST_LABELS} from './constants';

// Pre-TNSR-635 extraction screen, restored for the project_create_modal_redesign flag's OFF
// path. The current SowExtractionProgress was re-skinned in place (same component name, same
// file) as part of this ticket's Figma redesign — different colors, different text sizes,
// different file-card layout (no icon chip) — so reusing it would carry the new look into the
// legacy flow. Every styled-component below is defined locally with its original, pre-redesign
// CSS instead, so this screen renders pixel-for-pixel as it did in production before this PR.
// Unlike the current component, this one never renders a "no file" drop-zone state — the legacy
// flow's only entry point (ProjectCreationChoiceModal's onSow) always supplies a file up front,
// exactly as it did before this ticket, so `file` here is never null.

const ExtractionBody = Styled.div`
	padding: 24px; display: flex; flex-direction: column; gap: 16px;
`;

const ExtractionCard = Styled.div`
	background: ${BG_WHITE};
	border: 1px solid ${BORDER_INPUT};
	border-radius: 12px;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const FileCard = Styled.div`
	display: flex; align-items: center; gap: 12px;
	padding: 10px 12px; border: 1px solid ${BORDER_INPUT}; border-radius: 8px; background: ${BG_PANEL};
`;
const FileCardIcon = Styled.div`
	width: 36px; height: 36px; border-radius: 6px; background: ${ICON_BOX_BG};
	display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;
const FileCardMeta = Styled.div`flex: 1; min-width: 0;`;
const FileCardName = Styled.div`
	font-size: 13px; font-weight: 600; color: ${TEXT_MAIN};
	white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const FileCardSize = Styled.div`font-size: 11px; color: ${TEXT_AUX}; margin-top: 2px;`;
const FileCardReplace = Styled.button`
	font-size: 13px; font-weight: 600; color: ${NOVA_PURPLE};
	background: none; border: none; cursor: pointer; padding: 4px 8px; border-radius: 6px;
	&:hover { background: rgba(110,15,234,0.06); }
`;
const ExtractionStatus = Styled.div`
	display: flex; align-items: center; gap: 6px;
	font-size: 13px; color: ${TEXT_MAIN}; font-weight: 400;
	padding-bottom: 4px;
	border-bottom: 1px solid ${BORDER_DIVIDER};
`;
const SpinnerIcon = Styled.span`
	display: inline-flex;
	align-items: center;
	flex-shrink: 0;
	animation: legacy-sow-spin 1s linear infinite;
	@keyframes legacy-sow-spin { to { transform: rotate(360deg); } }
`;
const ChecklistContainer = Styled.div`display: flex; flex-direction: column; gap: 10px;`;
const ChecklistRow = Styled.div`display: flex; align-items: center; gap: 10px; font-size: 13px;`;
const ChecklistDot = Styled.div<{status: 'pending' | 'active' | 'done'}>`
	width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0;
	display: flex; align-items: center; justify-content: center; font-size: 10px;
	${({status}) =>
		status === 'done'
			? `background: ${SUCCESS_BRIGHT}; color: ${BG_WHITE};`
			: status === 'active'
			? `border: 2px solid ${NOVA_PURPLE}; background: rgba(110,15,234,0.08);`
			: `border: 1.5px solid ${BORDER_DOT_PENDING}; background: ${BG_DOT_PENDING};`}
`;
const ChecklistLabel = Styled.div<{status: 'pending' | 'active' | 'done'}>`
	color: ${({status}) => (status === 'pending' ? TEXT_AUX : TEXT_MAIN)};
	font-weight: ${({status}) => (status === 'active' ? '600' : '400')};
`;

const RejectionContainer = Styled.div`
	display: flex; flex-direction: column; align-items: center; text-align: center;
	gap: 8px; padding: 24px 8px;
`;
const RejectionIconWrap = Styled.div`
	width: 40px; height: 40px; border-radius: 50%; background: ${ERROR_BG};
	display: flex; align-items: center; justify-content: center; margin-bottom: 4px;
`;
const RejectionTitle = Styled.div`font-size: 14px; font-weight: 600; color: ${TEXT_MAIN};`;
const RejectionMessage = Styled.div`font-size: 13px; color: ${TEXT_MED}; max-width: 360px;`;

interface LegacySowExtractionProgressProps {
	file: AttachedFile;
	steps: SowExtractionStep[];
	onReplace: (file: AttachedFile) => void;
	rejectionMessage?: string | null;
}

function formatBytes(base64: string): string {
	const bytes = Math.round((base64.length * 3) / 4);
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const LegacySowExtractionProgress: React.FC<LegacySowExtractionProgressProps> = ({
	file,
	steps,
	onReplace,
	rejectionMessage,
}) => {
	const {formatMessage} = useIntl();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [replaceError, setReplaceError] = useState<string | null>(null);

	const handleReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		if (!f) return;
		setReplaceError(null);
		if (f.size > 20 * 1024 * 1024) {
			trackBaselineBuilderFileRejected(BUILDER_FILE_REJECT_REASON.OVERSIZED);
			setReplaceError(formatMessage({id: 'sow_upload.file_too_large'}));
			if (fileInputRef.current) fileInputRef.current.value = '';
			return;
		}
		const reader = new FileReader();
		reader.onload = evt => {
			const b64 = (evt.target?.result as string).split(',')[1] ?? '';
			if (fileInputRef.current) fileInputRef.current.value = '';
			trackBaselineBuilderDocumentUploaded(f);
			onReplace({filename: f.name, mimeType: f.type, dataBase64: b64});
		};
		reader.onerror = () => {
			trackBaselineBuilderFileRejected(BUILDER_FILE_REJECT_REASON.READ_ERROR);
			setReplaceError(formatMessage({id: 'sow_upload.file_read_error'}));
			if (fileInputRef.current) fileInputRef.current.value = '';
		};
		reader.readAsDataURL(f);
	};

	const activeStep = steps.find(s => s.status === 'active');
	const allDone = steps.length > 0 && steps.every(s => s.status === 'done');

	return (
		<ExtractionBody data-cy="legacy-sow-extraction">
			<ExtractionCard>
				<FileCard>
					<FileCardIcon>
						<Icon icon="deliverable" size="m" color="secondary" />
					</FileCardIcon>
					<FileCardMeta>
						<FileCardName title={file.filename}>{file.filename}</FileCardName>
						<FileCardSize>
							{formatBytes(file.dataBase64)} · {formatMessage({id: 'sow_upload.uploaded_just_now'})}
						</FileCardSize>
					</FileCardMeta>
					<FileCardReplace
						onClick={() => {
							setReplaceError(null);
							fileInputRef.current?.click();
						}}
					>
						{formatMessage({id: 'sow_upload.replace'})}
					</FileCardReplace>
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,.docx,.md"
						style={{display: 'none'}}
						onChange={handleReplaceFile}
					/>
				</FileCard>
				{replaceError && <div style={{fontSize: 12, color: ERROR_COLOR_2, padding: '2px 0'}}>{replaceError}</div>}

				{rejectionMessage ? (
					<RejectionContainer>
						<RejectionIconWrap>
							<Icon icon="warning" size="m" customColor={ERROR_COLOR_2} />
						</RejectionIconWrap>
						<RejectionTitle>{formatMessage({id: 'sow_upload.rejected_title'})}</RejectionTitle>
						<RejectionMessage>{rejectionMessage}</RejectionMessage>
					</RejectionContainer>
				) : (
					<>
						<ExtractionStatus>
							<SpinnerIcon>
								<Icon icon="reload" size="s" customColor={NOVA_PURPLE} />
							</SpinnerIcon>
							<Icon icon="sparkle" size="s" customColor={NOVA_PURPLE} />
							{allDone
								? formatMessage({id: 'sow_upload.nova_finalising'})
								: activeStep
								? `${formatMessage({id: 'sow_upload.nova_reading'})} ${
										SOW_CHECKLIST_LABELS[activeStep.label] ?? activeStep.label
								  }…`
								: formatMessage({id: 'sow_upload.nova_reading_sow'})}
						</ExtractionStatus>

						<ChecklistContainer>
							{steps.map(step => (
								<ChecklistRow key={step.label}>
									<ChecklistDot status={step.status}>
										{step.status === 'done' && <Icon icon="done" size="s" color="white" />}
									</ChecklistDot>
									<ChecklistLabel status={step.status}>
										{SOW_CHECKLIST_LABELS[step.label] ?? step.label}
									</ChecklistLabel>
								</ChecklistRow>
							))}
						</ChecklistContainer>
					</>
				)}
			</ExtractionCard>
		</ExtractionBody>
	);
};

export default LegacySowExtractionProgress;
